// Unified entity type profile: display name, icon, color, and aliases

export const TYPE_PROFILE = {
  objective: {
    value: "objective",
    label: "Objective",
    emoji: "🎯",
    icon: "objective",
    color: "#3B82F6",
  },
  platform: {
    value: "platform",
    label: "Platform",
    emoji: "🏗️",
    icon: "platform",
    color: "#8B5CF6",
  },
  initiative: {
    value: "initiative",
    label: "Initiative",
    emoji: "🚀",
    icon: "initiative",
    color: "#F59E0B",
  },
  organization: {
    value: "organization",
    label: "Organisation",
    emoji: "🏢",
    icon: "organization",
    color: "#6B7280",
  },
  "business-capability": {
    value: "business-capability",
    label: "Business Capability",
    emoji: "💪",
    icon: "business-capability",
    color: "#84CC16",
  },
  "business-context": {
    value: "business-context",
    label: "Business Context",
    emoji: "🏛️",
    icon: "business-context",
    color: "#10B981",
  },
  "data-object": {
    value: "data-object",
    label: "Data Object",
    emoji: "💾",
    icon: "data-object",
    color: "#EF4444",
  },
  application: {
    value: "application",
    label: "Application",
    emoji: "💻",
    icon: "application",
    color: "#06B6D4",
  },
  interface: {
    value: "interface",
    label: "Interface",
    emoji: "🔌",
    icon: "api",
    color: "#F97316",
  },
  provider: {
    value: "provider",
    label: "Provider",
    emoji: "🚚",
    icon: "supplier",
    color: "#6366F1",
  },
  "technology-component": {
    value: "technology-component",
    label: "Technology Component",
    emoji: "🔧",
    icon: "technology-component",
    color: "#EC4899",
  },
  /** Application-scoped logical blocks — excluded from map landing and explorer type picker. */
  "solution-component": {
    value: "solution-component",
    label: "Solution Component",
    emoji: "🧩",
    icon: "application",
    color: "#22D3EE",
    catalogHidden: true,
  },
};

/** Beginner-friendly browse order for the Your map gallery (top → bottom).
 * Business layer: who → why → change → how work runs → what the business can do.
 */
export const MAP_TILE_ORDER = [
  "organization",
  "objective",
  "initiative",
  "business-context",
  "business-capability",
  "application",
  "data-object",
  "interface",
  "platform",
  "technology-component",
  "provider",
];

export const MAP_TILE_ORDER_INDEX = Object.fromEntries(
  MAP_TILE_ORDER.map((value, index) => [value, index])
);

/** Section headers on Your map — business → apps → data → connections → technology. */
export const MAP_TILE_GROUPS = [
  {
    id: "business",
    title: "Business",
    hint: "Who you are, what you aim for, how you change, and what the business can do",
    types: [
      "organization",
      "objective",
      "initiative",
      "business-context",
      "business-capability",
    ],
  },
  {
    id: "applications",
    title: "Applications",
    hint: "Software and systems people use",
    types: ["application"],
  },
  {
    id: "data",
    title: "Data",
    hint: "Information your organisation creates and relies on",
    types: ["data-object"],
  },
  {
    id: "connections",
    title: "Connections",
    hint: "How systems talk to each other",
    types: ["interface"],
  },
  {
    id: "technology",
    title: "Technology",
    hint: "Platforms, building blocks, and vendors behind the scenes",
    types: ["platform", "technology-component", "provider"],
  },
];

/** One-line helper shown on empty tiles so beginners know what belongs here. */
export const MAP_TILE_HINTS = {
  organization: "Teams, departments, or companies",
  objective: "Goals the business is working toward",
  initiative: "Projects or programmes driving change",
  "business-context": "Business processes and how work gets done",
  "business-capability": "What the business can do — e.g. order fulfilment",
  application: "Apps, portals, and internal systems",
  "data-object": "Customer records, orders, reports, and other data",
  interface: "APIs and integrations between systems",
  platform: "Cloud, hosting, or runtime environments",
  "technology-component": "Databases, frameworks, and specific tech",
  provider: "Vendors and external service providers",
};

export const getProfileByValue = (value) => TYPE_PROFILE[value] || null;

/** True when the type can be created from catalog / relationship pickers (excludes solution-component, etc.). */
export const isCatalogCreatableEntityType = (entityType) => {
  const key = String(entityType || "").trim().toLowerCase();
  if (!key) return false;
  const profile = getProfileByValue(key);
  return Boolean(profile && !profile.catalogHidden);
};

/** Types shown on map landing and explorer browse pickers (excludes application-scoped types). */
export const getCatalogEntityTypes = () =>
  Object.values(TYPE_PROFILE)
    .filter((profile) => !profile.catalogHidden)
    .sort(
      (a, b) =>
        (MAP_TILE_ORDER_INDEX[a.value] ?? 999) -
          (MAP_TILE_ORDER_INDEX[b.value] ?? 999) ||
        (a.label || "").localeCompare(b.label || "", "en", {
          sensitivity: "base",
        })
    );

/** Entity types available in application enrichment review (SC never offered). */
export const getEnrichmentReviewEntityTypes = () =>
  Object.values(TYPE_PROFILE).filter(
    (profile) => profile.value !== "solution-component" && !profile.catalogHidden
  );

export const getTypeColors = () => {
  const colors = {};
  Object.values(TYPE_PROFILE).forEach((p) => {
    colors[p.value] = { bg: p.color, border: p.color };
  });
  return colors;
};
